/**
 * Region Types
 * Types and interfaces for geographic region management
 */

// Region status enum
export type RegionStatus = "active" | "inactive" | "coming_soon" | "deprecated" | "hidden";

// Region visibility options
export type RegionVisibility =
  | "public" // Visible to all users
  | "registered" // Visible only to logged-in users
  | "admin" // Visible only to admins
  | "hidden"; // Not visible at all

// Region code type - all supported regions
export type RegionCode =
  | "iran"
  | "iraq"
  | "syria"
  | "kurdistan"
  | "afghanistan"
  | "baluchistan"
  | "turkey"
  | "armenia"
  | "azerbaijan"
  | "turkmenistan"
  | "pakistan"
  | "custom"
  | string; // Allow any string for dynamic region codes

// Region display names mapping
export const REGION_DISPLAY_NAMES: Record<string, string> = {
  iran: "Iran",
  iraq: "Iraq",
  syria: "Syria",
  kurdistan: "Kurdistan",
  afghanistan: "Afghanistan",
  baluchistan: "Baluchistan",
  turkey: "Turkey",
  armenia: "Armenia",
  azerbaijan: "Azerbaijan",
  turkmenistan: "Turkmenistan",
  pakistan: "Pakistan",
  custom: "Custom Region",
};

// Region status labels
export const REGION_STATUS_LABELS: Record<RegionStatus, string> = {
  active: "Active",
  inactive: "Inactive",
  coming_soon: "Coming Soon",
  deprecated: "Deprecated",
  hidden: "Hidden",
};

// Region visibility labels
export const REGION_VISIBILITY_LABELS: Record<RegionVisibility, string> = {
  public: "Public",
  registered: "Registered Users",
  admin: "Admin Only",
  hidden: "Hidden",
};

// Status colors for UI
export const REGION_STATUS_COLORS: Record<RegionStatus, string> = {
  active: "#48bb78",
  inactive: "#a0aec0",
  coming_soon: "#ed8936",
  deprecated: "#e53e3e",
  hidden: "#718096",
};

// Visibility colors for UI
export const REGION_VISIBILITY_COLORS: Record<RegionVisibility, string> = {
  public: "#48bb78",
  registered: "#4299e1",
  admin: "#ed8936",
  hidden: "#e53e3e",
};

// Coordinate type for boundaries
export interface BoundaryCoordinate {
  lat: number;
  lng: number;
}

// Viewport settings for a region
export interface RegionViewport {
  center: BoundaryCoordinate;
  zoom: number;
  minZoom?: number;
  maxZoom?: number;
}

// Geographic boundary definition
export interface Boundary {
  type: "Polygon" | "MultiPolygon";
  coordinates: BoundaryCoordinate[][]; // Array of coordinate rings
}

// Historical period for a region
export interface RegionHistoricalPeriod {
  id: string;
  name: string;
  startYear?: number;
  endYear?: number;
  description?: string;
  boundary?: Boundary;
}

// Feature flags for regions
export interface RegionFeatures {
  canCreateMaps: boolean;
  canViewPublicMaps: boolean;
  canUseTemplates: boolean;
  hasHistoricalData: boolean;
  historicalDataEnabled?: boolean;
  mapCreationEnabled?: boolean;
  socialFeaturesEnabled?: boolean;
}

// Default feature flags
export const DEFAULT_REGION_FEATURES: RegionFeatures = {
  canCreateMaps: true,
  canViewPublicMaps: true,
  canUseTemplates: true,
  hasHistoricalData: false,
  historicalDataEnabled: false,
  mapCreationEnabled: true,
  socialFeaturesEnabled: true,
};

// Main Region interface
export interface Region {
  id: string;
  code: RegionCode;
  name: string;
  localizedNames: Record<string, string>; // e.g., { "fa": "ایران", "ar": "إيران" }
  description?: string;
  boundary?: Boundary; // Optional - borders are loaded from JSON files via getRegionBorder()
  viewport: RegionViewport;
  area?: number; // Square kilometers
  status: RegionStatus;
  visibility: RegionVisibility;
  historicalPeriods?: RegionHistoricalPeriod[];
  features: RegionFeatures;
  parentRegionCode?: RegionCode;
  childRegionCodes?: RegionCode[];
  parentRegions?: string[]; // Parent region IDs
  childRegions?: string[]; // Child region IDs
  tags?: string[];
  createdAt: string;
  updatedAt: string;
  order?: number;
  defaultMapStyle?: string;
  themeColor?: string;
  flagEmoji?: string;
  thumbnailUrl?: string;
  isDefault?: boolean;
}

// Filter options for querying regions
export interface RegionFilter {
  status?: RegionStatus[];
  visibility?: RegionVisibility[];
  search?: string;
  tags?: string[];
  parentRegionCode?: RegionCode;
  parentRegion?: string; // Alternative filter field
  hasHistoricalData?: boolean;
}

// Region statistics
export interface RegionStats {
  mapsCount?: number;
  publicMapsCount?: number;
  usersCount?: number;
  totalMaps?: number;
  publicMaps?: number;
  totalUsers?: number;
  activeUsers?: number;
  totalViews?: number;
}

// Region with statistics (for admin views)
export interface RegionWithStats extends Region {
  mapsCount: number;
  publicMapsCount: number;
  usersCount: number;
  stats?: RegionStats;
}

// Region summary (simplified region for lists)
export interface RegionSummary {
  id: string;
  code: RegionCode;
  name: string;
  status: RegionStatus;
  visibility: RegionVisibility;
  flagEmoji?: string;
  mapsCount?: number;
}

// Input types for creating/updating regions
export interface CreateRegionInput {
  code: RegionCode;
  name: string;
  localizedNames?: Record<string, string>;
  description?: string;
  boundary?: Boundary; // Optional - borders are loaded from JSON files
  viewport: RegionViewport;
  area?: number;
  status?: RegionStatus;
  visibility?: RegionVisibility;
  features?: Partial<RegionFeatures>;
  tags?: string[];
  isDefault?: boolean;
  defaultMapStyle?: string;
  themeColor?: string;
  flagEmoji?: string;
  thumbnailUrl?: string;
  historicalPeriods?: RegionHistoricalPeriod[];
  parentRegions?: string[];
  childRegions?: string[];
  order?: number;
}

export interface UpdateRegionInput {
  name?: string;
  localizedNames?: Record<string, string>;
  description?: string;
  boundary?: Boundary;
  viewport?: RegionViewport;
  area?: number;
  status?: RegionStatus;
  visibility?: RegionVisibility;
  features?: Partial<RegionFeatures>;
  tags?: string[];
  themeColor?: string;
  flagEmoji?: string;
  thumbnailUrl?: string;
  isDefault?: boolean;
  defaultMapStyle?: string;
  historicalPeriods?: RegionHistoricalPeriod[];
  parentRegions?: string[];
  childRegions?: string[];
  order?: number;
}

// Region viewports mapping (for quick access)
export const REGION_VIEWPORTS: Record<string, RegionViewport> = {
  iran: { center: { lat: 32.4279, lng: 53.688 }, zoom: 5 },
  iraq: { center: { lat: 33.2232, lng: 43.6793 }, zoom: 6 },
  syria: { center: { lat: 34.8021, lng: 38.9968 }, zoom: 6 },
  kurdistan: { center: { lat: 36.2419, lng: 43.7515 }, zoom: 6 },
  afghanistan: { center: { lat: 33.9391, lng: 67.71 }, zoom: 6 },
  baluchistan: { center: { lat: 27.5, lng: 64.0 }, zoom: 6 },
  turkey: { center: { lat: 38.9637, lng: 35.2433 }, zoom: 5 },
  armenia: { center: { lat: 40.0691, lng: 45.0382 }, zoom: 7 },
  azerbaijan: { center: { lat: 40.1431, lng: 47.5769 }, zoom: 7 },
  turkmenistan: { center: { lat: 38.9697, lng: 59.5563 }, zoom: 6 },
  pakistan: { center: { lat: 30.3753, lng: 69.3451 }, zoom: 5 },
  custom: { center: { lat: 32.0, lng: 53.0 }, zoom: 5 },
};
